import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Place,
  Refresh,
} from '@mui/icons-material';
import { getHotspots, createHotspot, updateHotspot, deleteHotspot } from '../services/hotspots';
import HotspotFormDialog from './HotspotFormDialog';

const Hotspots = () => {
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchHotspots();
  }, []);

  const fetchHotspots = async () => {
    setLoading(true);
    try {
      const response = await getHotspots();
      setHotspots(response || []);
    } catch (err) {
      setError('Failed to fetch hotspots');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredHotspots = hotspots.filter((hotspot) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      hotspot.name?.toLowerCase().includes(searchLower) ||
      hotspot.city?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddHotspot = () => {
    setSelectedHotspot(null);
    setIsEdit(false);
    setFormDialogOpen(true);
  };

  const handleEditHotspot = (hotspot) => {
    setSelectedHotspot(hotspot);
    setIsEdit(true);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (hotspot) => {
    setSelectedHotspot(hotspot);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (isEdit) {
        await updateHotspot(selectedHotspot.id, formData);
        setSuccess('Hotspot updated successfully!');
      } else {
        await createHotspot(formData);
        setSuccess('Hotspot created successfully!');
      }
      setFormDialogOpen(false);
      fetchHotspots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(isEdit ? 'Failed to update hotspot' : 'Failed to create hotspot');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteHotspot(selectedHotspot.id);
      setSuccess('Hotspot deleted successfully!');
      setDeleteDialogOpen(false);
      fetchHotspots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete hotspot');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Hotspots Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          Manage hotspot locations
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search hotspots..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.02)',
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchHotspots}
                sx={{
                  color: '#6b7280',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  '&:hover': { color: '#52ad83', borderColor: '#52ad83' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddHotspot}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                boxShadow: '0 4px 15px rgba(82, 173, 131, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
                  boxShadow: '0 6px 20px rgba(82, 173, 131, 0.4)',
                },
              }}
            >
              Add New Hotspot
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {success && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <Typography sx={{ color: '#16a34a' }}>{success}</Typography>
        </Paper>
      )}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'rgba(79, 70, 229, 0.05)',
                      '& th': {
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Latitude</TableCell>
                    <TableCell>Longitude</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHotspots.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Place sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                            No hotspots found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {searchTerm
                              ? 'Try adjusting your search criteria'
                              : 'Get started by adding a new hotspot'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHotspots
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((hotspot, index) => (
                        <TableRow
                          key={hotspot.id}
                          sx={{
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(0,0,0,0.05)',
                              py: 2,
                              color: '#374151',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              #{hotspot.id || index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {hotspot.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{hotspot.city}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{hotspot.latitude}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{hotspot.longitude}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {hotspot.createdAt
                                ? new Date(hotspot.createdAt).toLocaleDateString()
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditHotspot(hotspot)}
                                  sx={{
                                    color: '#6b7280',
                                    '&:hover': { color: '#4f46e5' },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(hotspot)}
                                  sx={{
                                    color: '#6b7280',
                                    '&:hover': { color: '#dc2626' },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredHotspots.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: '#6b7280',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTablePagination-select': {
                  color: '#374151',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#6b7280',
                },
                '& .MuiTablePagination-actions button': {
                  color: '#6b7280',
                  '&:hover': {
                    color: '#4f46e5',
                    background: 'rgba(79, 70, 229, 0.08)',
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      <HotspotFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        hotspot={selectedHotspot}
        isEdit={isEdit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#dc2626',
          }}
        >
          <Delete />
          Delete Hotspot
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#6b7280' }}>
            Are you sure you want to delete <strong>{selectedHotspot?.name}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
            }}
          >
            Delete Hotspot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Hotspots;
